import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";
import RouteSelector from "@/components/sections/RouteSelector";

const NAV = [
  { label: "How We Help", id: "how-we-help" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Why Us", id: "why-us" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (!onLanding) {
      navigate(`/#${id}`);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const transparent = !scrolled && onLanding && !open;
  const textColor = transparent ? "text-white" : "text-[#451C25]";
  const navLink = `text-[12px] uppercase tracking-[0.16em] ${textColor} hover:text-[#B3A17D] transition-colors whitespace-nowrap`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent ? "bg-transparent border-b border-transparent" : "backdrop-blur-xl bg-[#F5EFE7]/95 border-b border-[#451C25]/15 shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="shrink-0">
          <BrandLogo
            variant="endorsement"
            size="sm"
            color={transparent ? "#F5EFE7" : "#451C25"}
            markVariant={transparent ? "ivory" : "burgundy"}
            subColor={transparent ? "rgba(245,239,231,0.7)" : "#897756"}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => goToSection(n.id)} className={navLink}>
              {n.label}
            </button>
          ))}
          <Link to="/resources" data-testid="nav-resources" className={navLink}>
            Resources
          </Link>
        </nav>

        <div className="hidden lg:flex items-center shrink-0">
          <ScheduleCallButton size="sm" source="header" tone={transparent ? "sand" : "burgundy"} testId="header-cta-book" />
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden p-2 ${textColor}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <RouteSelector transparent={transparent} />

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F5EFE7] border-t border-[#451C25]/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => goToSection(n.id)} className="text-left text-sm uppercase tracking-[0.18em] text-[#451C25]">
                {n.label}
              </button>
            ))}
            <Link to="/resources" data-testid="mobile-nav-resources" onClick={() => setOpen(false)} className="text-left text-sm uppercase tracking-[0.18em] text-[#451C25]">
              Resources
            </Link>
            <ScheduleCallButton source="mobile_header" tone="burgundy" testId="mobile-cta-book" className="mt-2 w-full" />
          </div>
        </div>
      )}
    </header>
  );
}
